import React, { useEffect, useRef } from 'react';

interface TemplateReferencePreviewProps {
  /** URL of the template's preview video (full render example). */
  previewVideoUrl: string;
  /** Time in seconds to seek to when the loop starts. */
  startTimeSeconds: number;
  /** Duration in seconds to loop before looping back to `startTimeSeconds`. */
  durationSeconds: number;
  /** Optional human-readable description shown above the preview. */
  label?: string;
}

/**
 * Shows a looping clip from the template's preview video at the exact position
 * where a slot appears, so operators can visually anchor their upload to the
 * final rendered output.
 *
 * The component seeks `previewVideoUrl` to `startTimeSeconds` on load and loops
 * the segment [`startTimeSeconds`, `startTimeSeconds + durationSeconds`].
 */
const TemplateReferencePreview: React.FC<TemplateReferencePreviewProps> = ({
  previewVideoUrl,
  startTimeSeconds,
  durationSeconds,
  label,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const endTime = startTimeSeconds + durationSeconds;

  // Seek to startTimeSeconds whenever the source or window changes.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!Number.isFinite(v.duration) || v.duration <= 0) return;
    v.currentTime = startTimeSeconds;
    const maybePlay = v.play();
    if (maybePlay && typeof maybePlay.catch === 'function') {
      maybePlay.catch(() => undefined);
    }
  }, [startTimeSeconds, durationSeconds, previewVideoUrl]);

  return (
    <div className="mt-3">
      <p className="text-xs text-text-secondary mb-1">
        {label ?? 'Template reference — where this slot appears'}
      </p>
      <div className="relative rounded-md overflow-hidden bg-bg-secondary border border-border-subtle">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          key={`${previewVideoUrl}-${startTimeSeconds}-${durationSeconds}`}
          src={previewVideoUrl}
          muted
          playsInline
          preload="metadata"
          style={{ width: '100%', height: 'auto', display: 'block' }}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            v.currentTime = startTimeSeconds;
            const maybePlay = v.play();
            if (maybePlay && typeof maybePlay.catch === 'function') {
              maybePlay.catch(() => undefined);
            }
          }}
          onTimeUpdate={(e) => {
            const v = e.currentTarget;
            if (v.currentTime >= endTime || v.currentTime < startTimeSeconds) {
              v.currentTime = startTimeSeconds;
              const maybePlay = v.play();
              if (maybePlay && typeof maybePlay.catch === 'function') {
                maybePlay.catch(() => undefined);
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default TemplateReferencePreview;
